// app/frontend/lib/theme.ts
// Theme MODE is three-way — light | dark | system — per the theme-mode-switcher
// design handoff (2026-09-06): system follows the OS `prefers-color-scheme`
// and tracks it live. The default mode is SYSTEM (product decision 2026-09-06;
// the canvas prototype defaults to dark — divergence ledgered in
// doc/design/README.md). Values "light"/"dark" written by the old two-way
// toggle read back as explicit modes, so nobody's stored choice is reset.
// The server renders no theme class; `ak-light` is applied pre-paint from the
// effective theme. Persisted client-side only — theme is not a server concern
// (spec §3.4).
const KEY = "fd-theme"

export type ThemeMode = "light" | "dark" | "system"

// The `t` key walks this order (handoff: light → dark → system → light).
const CYCLE: readonly ThemeMode[] = ["light", "dark", "system"]

export function themeMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(KEY)
    if (stored === "light" || stored === "dark" || stored === "system") return stored
  } catch {
    /* storage unavailable */
  }
  return "system"
}

function prefersDark(): boolean {
  try {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
  } catch {
    // No matchMedia — fall back to the product's dark look.
    return true
  }
}

export function effectiveTheme(mode: ThemeMode = themeMode()): "light" | "dark" {
  if (mode === "system") return prefersDark() ? "dark" : "light"
  return mode
}

function apply(mode: ThemeMode) {
  document.documentElement.classList.toggle("ak-light", effectiveTheme(mode) === "light")
}

export function setThemeMode(mode: ThemeMode) {
  try {
    localStorage.setItem(KEY, mode)
  } catch {
    /* storage unavailable — applies for this page, resets to system next load */
  }
  apply(mode)
}

export function cycleThemeMode(): ThemeMode {
  const next = CYCLE[(CYCLE.indexOf(themeMode()) + 1) % CYCLE.length]
  setThemeMode(next)
  return next
}

export function initTheme() {
  apply(themeMode())
  // One document-wide listener, registered once at boot rather than per
  // component mount: the theme lands on <html>, above any React tree, and it
  // must keep following the OS on chrome-less BareLayout screens that render
  // no switcher at all. Re-checks the mode at fire time so a listener
  // registered under `system` goes quiet after an explicit choice.
  try {
    window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
      if (themeMode() === "system") apply("system")
    })
  } catch {
    /* matchMedia unavailable — the fallback above already picked dark */
  }
}
